/**
 * Template System TypeScript Types
 * Defines types for block-based WhatsApp message templates
 */

// =====================================================
// TEMPLATE BLOCK TYPES
// =====================================================

/**
 * Text block with optional variable placeholders
 * Variables follow the {{variable_name}} pattern
 */
export interface TextBlock {
  type: 'text';
  content: string;
}

/**
 * Image block with storage URL and optional caption
 */
export interface ImageBlock {
  type: 'image';
  storage_url: string;
  storage_path?: string;
  caption?: string;
  width?: number;
  height?: number;
}

/**
 * Link block with URL and display text
 */
export interface LinkBlock {
  type: 'link';
  url: string;
  text?: string;
}

/**
 * Union type for all block types
 */
export type TemplateBlock = TextBlock | ImageBlock | LinkBlock;

/**
 * Template content structure
 */
export interface TemplateContent {
  blocks: TemplateBlock[];
}

// =====================================================
// DATABASE MODELS
// =====================================================

/**
 * Template Category
 */
export interface TemplateCategory {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  color?: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

/**
 * Template Tag
 */
export interface TemplateTag {
  id: string;
  user_id: string;
  name: string;
  created_at: string;
}

/**
 * Template (full database model)
 */
export interface Template {
  id: string;
  user_id: string;
  category_id?: string;
  name: string;
  description?: string;
  content: TemplateContent;
  variables: string[];
  is_favorite: boolean;
  tags: string[];
  usage_count: number;
  last_used_at?: string;
  deleted_at?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Template with category populated
 */
export interface TemplateWithCategory extends Template {
  category?: TemplateCategory;
}

/**
 * Template Image
 */
export interface TemplateImage {
  id: string;
  template_id: string;
  user_id: string;
  storage_path: string;
  storage_bucket: string;
  filename: string;
  mime_type: string;
  file_size: number;
  width?: number;
  height?: number;
  caption?: string;
  uploaded_at: string;
}

// =====================================================
// API REQUEST/RESPONSE TYPES
// =====================================================

/**
 * Create Template Request
 */
export interface CreateTemplateRequest {
  name: string;
  description?: string;
  category_id?: string;
  content: TemplateContent;
  tags?: string[];
  is_favorite?: boolean;
}

/**
 * Update Template Request
 */
export interface UpdateTemplateRequest {
  name?: string;
  description?: string;
  category_id?: string;
  content?: TemplateContent;
  tags?: string[];
  is_favorite?: boolean;
}

/**
 * Template List Query Parameters
 */
export interface TemplateListParams {
  category_id?: string;
  tags?: string[];
  is_favorite?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
  sort_by?: 'created_at' | 'updated_at' | 'name' | 'usage_count';
  sort_order?: 'asc' | 'desc';
}

/**
 * Template List Response
 */
export interface TemplateListResponse {
  templates: TemplateWithCategory[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Create Category Request
 */
export interface CreateCategoryRequest {
  name: string;
  description?: string;
  color?: string;
  sort_order?: number;
}

/**
 * Update Category Request
 */
export interface UpdateCategoryRequest {
  name?: string;
  description?: string;
  color?: string;
  sort_order?: number;
}

/**
 * Create Tag Request
 */
export interface CreateTagRequest {
  name: string;
}

// =====================================================
// TEMPLATE RENDERING TYPES
// =====================================================

/**
 * Variable values for template rendering
 * Maps variable names to their values
 */
export interface TemplateVariables {
  [key: string]: string | number | undefined;
}

/**
 * Rendered template block (after variable substitution)
 */
export interface RenderedTextBlock {
  type: 'text';
  content: string;
}

export interface RenderedImageBlock {
  type: 'image';
  url: string;
  caption?: string;
}

export interface RenderedLinkBlock {
  type: 'link';
  url: string;
  text: string;
}

export type RenderedBlock = RenderedTextBlock | RenderedImageBlock | RenderedLinkBlock;

/**
 * Rendered template ready for WhatsApp sending
 */
export interface RenderedTemplate {
  blocks: RenderedBlock[];
  text: string; // Combined text from all blocks
  images: Array<{ url: string; caption?: string }>;
  links: Array<{ url: string; text: string }>;
}

// =====================================================
// WHATSAPP INTEGRATION TYPES
// =====================================================

/**
 * Send Template Message Request
 */
export interface SendTemplateMessageRequest {
  template_id: string;
  client_id?: string; // If provided, auto-fill variables from client
  jid: string; // WhatsApp JID to send to
  variables?: TemplateVariables; // Override or additional variables
}

/**
 * Send Template Message Response
 */
export interface SendTemplateMessageResponse {
  success: boolean;
  message_id?: string;
  error?: string;
}

// =====================================================
// VALIDATION TYPES
// =====================================================

/**
 * Template validation result
 */
export interface TemplateValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Template constraints
 */
export const TEMPLATE_CONSTRAINTS = {
  MAX_NAME_LENGTH: 200,
  MAX_DESCRIPTION_LENGTH: 1000,
  MAX_TEXT_CONTENT_LENGTH: 4096, // WhatsApp limit
  MAX_IMAGES_PER_TEMPLATE: 5,
  MAX_IMAGE_SIZE_BYTES: 5 * 1024 * 1024, // 5MB
  MAX_TEMPLATES_PER_USER: 100,
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  VARIABLE_PATTERN: /\{\{([a-zA-Z0-9_]+)\}\}/g,
} as const;

/**
 * Common template variables available from client data
 */
export const COMMON_VARIABLES = {
  // Client fields
  name: 'Client name',
  first_name: 'Client first name',
  phone: 'Client phone number',
  email: 'Client email',
  age: 'Client age',
  gender: 'Client gender',
  goal: 'Client goal',

  // Dietitian fields
  dietitian_name: 'Dietitian name',
  dietitian_phone: 'Dietitian phone',
  dietitian_organization: 'Organization name',

  // Dynamic fields
  date: 'Current date',
  time: 'Current time',
} as const;

export type CommonVariableName = keyof typeof COMMON_VARIABLES;

// =====================================================
// UTILITY TYPES
// =====================================================

/**
 * Extract variable names from template content
 */
export type ExtractedVariables = string[];

/**
 * Template statistics
 */
export interface TemplateStats {
  total_templates: number;
  total_categories: number;
  total_tags: number;
  total_usage: number;
  most_used_template?: Template;
  recently_used: Template[];
}

/**
 * Upload image request
 */
export interface UploadTemplateImageRequest {
  template_id: string;
  file: File;
  caption?: string;
}

/**
 * Upload image response
 */
export interface UploadTemplateImageResponse {
  image: TemplateImage;
  storage_url: string;
}
